import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { LoroDoc } from "loro-crdt";
import { Moon } from "lucide-react";
import { Button, Dialog, Floating, Input, usePopover } from "../ui";
import { archiveTask, quietSetAside, setAside, unarchiveTask } from "../core/ops";
import type { Task } from "../core/schema";
import { useToasts } from "../ui/toasts";
import { QUIET_DAYS, returnPoints, shouldAsk } from "./notNowLogic";

/**
 * *Not now* choices (for a menu): a return point, or a day. Calls `onSet` with the count the task reached, so the
 * caller can ask the question.
 */
export function NotNowChoices({
  task,
  write,
  onSet,
}: {
  task: Task;
  write(fn: (doc: LoroDoc) => void): void;
  onSet(count: number): void;
}) {
  const { t } = useTranslation();
  const [day, setDay] = useState("");
  const set = (until: Date) => {
    let count = 0;
    write((doc) => {
      count = setAside(doc, task.id, until);
    });
    onSet(count);
  };
  const item = "w-full rounded-sm px-2 py-1 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800";
  return (
    <>
      <li className="px-2 pt-1 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
        {t("notNow.label")}
      </li>
      {returnPoints(new Date()).map((p) => (
        <li key={p.key}>
          <button
            type="button"
            role="menuitem"
            className={item}
            onClick={() => set(p.at)}
            data-testid={`not-now-${p.key}`}
          >
            {t(`notNow.${p.key}`)}
          </button>
        </li>
      ))}
      <li className="flex items-center gap-1 px-2 py-1">
        <Input
          compact
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          aria-label={t("notNow.onDay")}
          data-testid="not-now-day"
        />
        <Button
          size="sm"
          variant="secondary"
          disabled={!day}
          onClick={() => set(new Date(`${day}T08:00:00`))}
          data-testid="not-now-day-set"
        >
          {t("notNow.set")}
        </Button>
      </li>
    </>
  );
}

/**
 * The question after the third *Not now* in a row: it asks, it never scolds. Each choice is one step; *Keep as is*
 * silences it for a while.
 */
export function NotNowQuestion({
  task,
  shared,
  write,
  openTask,
  onClose,
}: {
  task: Task;
  shared: boolean;
  write(fn: (doc: LoroDoc) => void): void;
  openTask(id: string): void;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const pushToast = useToasts((s) => s.push);
  const open = () => {
    openTask(task.id);
    onClose();
  };
  const choice =
    "flex flex-col items-start gap-0.5 rounded-md border border-neutral-200 px-3 py-2 text-left hover:border-neutral-700 dark:border-neutral-800";
  return (
    <Dialog title={t("notNow.askTitle")} onClose={onClose}>
      <div className="flex flex-col gap-2" data-testid="not-now-question">
        <p className="text-[13px] text-neutral-600 dark:text-neutral-300">
          {t("notNow.askText", { title: task.title })}
        </p>
        <button type="button" className={choice} onClick={open} data-testid="not-now-smaller">
          <span className="text-sm font-medium">{t("notNow.smaller")}</span>
          <span className="text-[12.5px] text-neutral-500">{t("notNow.smallerHint")}</span>
        </button>
        {task.typeId === "recurring" && (
          <button type="button" className={choice} onClick={open} data-testid="not-now-how-often">
            <span className="text-sm font-medium">{t("notNow.howOften")}</span>
            <span className="text-[12.5px] text-neutral-500">{t("notNow.howOftenHint")}</span>
          </button>
        )}
        {shared && (
          <button type="button" className={choice} onClick={open} data-testid="not-now-hand-over">
            <span className="text-sm font-medium">{t("notNow.handOver")}</span>
            <span className="text-[12.5px] text-neutral-500">{t("notNow.handOverHint")}</span>
          </button>
        )}
        <button
          type="button"
          className={choice}
          onClick={() => {
            write((doc) => archiveTask(doc, task.id));
            pushToast(t("task.archivedToast", { title: task.title }), {
              action: { label: t("common.undo"), run: () => write((doc) => unarchiveTask(doc, task.id)) },
            });
            onClose();
          }}
          data-testid="not-now-let-go"
        >
          <span className="text-sm font-medium">{t("notNow.letGo")}</span>
          <span className="text-[12.5px] text-neutral-500">{t("notNow.letGoHint")}</span>
        </button>
        <button
          type="button"
          className={choice}
          onClick={() => {
            write((doc) => quietSetAside(doc, task.id, new Date(Date.now() + QUIET_DAYS * 86_400_000)));
            onClose();
          }}
          data-testid="not-now-keep"
        >
          <span className="text-sm font-medium">{t("notNow.keep")}</span>
          <span className="text-[12.5px] text-neutral-500">{t("notNow.keepHint")}</span>
        </button>
      </div>
    </Dialog>
  );
}

/** *Not now* as a button with its choices (the task panel): any task, set aside until a return point. */
export function NotNowButton({
  task,
  shared,
  write,
  openTask,
}: {
  task: Task;
  shared: boolean;
  write(fn: (doc: LoroDoc) => void): void;
  openTask(id: string): void;
}) {
  const { t } = useTranslation();
  const { open, toggle, setOpen, ref, panelRef } = usePopover<HTMLDivElement>();
  const [asking, setAsking] = useState(false);
  return (
    <div ref={ref} className="relative">
      <Button
        variant="ghost"
        size="sm"
        aria-expanded={open}
        onClick={toggle}
        data-testid="task-not-now"
        title={t("notNow.label")}
      >
        <Moon size={14} aria-hidden /> {t("notNow.label")}
      </Button>
      {open && (
        <Floating
          anchor={ref}
          panelRef={panelRef}
          align="end"
          className="w-60 rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-sm shadow-[var(--shadow-md)] dark:border-neutral-700"
        >
          <ul role="menu">
            <NotNowChoices
              task={task}
              write={write}
              onSet={(count) => {
                setOpen(false);
                if (shouldAsk(task, count, Date.now())) setAsking(true);
              }}
            />
          </ul>
        </Floating>
      )}
      {asking && (
        <NotNowQuestion
          task={task}
          shared={shared}
          write={write}
          openTask={openTask}
          onClose={() => setAsking(false)}
        />
      )}
    </div>
  );
}
